import { useId, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useLocale, type MessageKey } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import type { StaffRefundResult } from '../../lib/api/staffRefund';

// ═══════════════════════════════════════════════════════════════════
// «AVBESTILL OG REFUNDER» — THE CONFIRMATION (#284).
//
// Staff cancel a PAID booking; the server refunds the whole payment and
// deletes the booking (`POST /organization/bookings/:id/refund`). This asks
// first, through the shared `Dialog` — never `window.confirm()`
// (DOMAIN_RULES) — and shows the server's answer as a sentence.
//
// THE AMOUNT IS THE SERVER'S, twice: what the dialog promises is the payment's
// amount from `GET /bookings` (`paidAmount`), and what it says afterwards is
// what the refund route reports it refunded. Neither is worked out here.
//
// «AVBESTILT» ONLY ON A 200. A 202 is a refund the provider has not confirmed
// — the court is still held — and the dialog says exactly that.
//
// A sibling of the edit modal's own dialog, with the same stacking as the
// delete confirmation, so it is the innermost open dialog. Focus starts on
// «Behold bookingen», the first control, so a stray Enter never refunds.
// ═══════════════════════════════════════════════════════════════════

export interface StaffRefundDialogProps {
  /** The payment's amount, from the server, or null when it did not say. */
  amount: { amountMinor: number; currency: string } | null;
  /** How many equipment bookings ride with this one (they go with it). */
  attachedAssets: number;
  /** Sends the refund. Resolves with the route's answer. */
  onRefund: () => Promise<StaffRefundResult>;
  /** Close the dialog, the booking still standing (or its fate unknown). */
  onClose: () => void;
  /** The booking is gone — refunded now, or already cancelled. Closes the edit modal. */
  onDone: () => void;
}

/** Server CODES → a sentence. The codes are never shown raw. */
const REFUSAL: Record<string, MessageKey> = {
  refund_in_progress: 'booking.edit.refund.inProgress',
  refund_failed: 'booking.edit.refund.failed',
  state_moved: 'booking.edit.refund.stateMoved',
  refunds_unavailable: 'booking.edit.refund.unavailable',
  not_paid: 'booking.edit.refund.notPaid',
  reauthentication_required: 'booking.edit.refund.reauth',
  forbidden: 'booking.edit.refund.forbidden',
};

/** Answers after which pressing again can help: the same press, a moment later. */
const RETRYABLE = new Set(['refund_in_progress', 'refund_failed', 'reauthentication_required']);

type Phase =
  | { kind: 'asking' }
  | { kind: 'working' }
  | { kind: 'done'; text: string }
  /** `retry`: the confirm button is offered again. */
  | { kind: 'message'; text: string; retry: boolean };

export function StaffRefundDialog({
  amount,
  attachedAssets,
  onRefund,
  onClose,
  onDone,
}: StaffRefundDialogProps) {
  const { t, formatNumber } = useLocale();
  const uid = safeId('staff-refund', useId());
  const titleId = `${uid}-title`;
  const bodyId = `${uid}-body`;
  const [phase, setPhase] = useState<Phase>({ kind: 'asking' });

  const money = (minor: number, currency: string) =>
    formatNumber(minor / 100, { style: 'currency', currency, minimumFractionDigits: 0 });

  const confirm = async () => {
    setPhase({ kind: 'working' });
    const r = await onRefund();
    if (r.state === 'refunded') {
      setPhase({
        kind: 'done',
        text: t('booking.edit.refund.done', { amount: money(r.refundMinor, r.currency) }),
      });
      return;
    }
    if (r.state === 'refused' && r.code === 'already_cancelled') {
      setPhase({ kind: 'done', text: t('booking.edit.refund.alreadyCancelled') });
      return;
    }
    // THE BOOKING IS GONE, SO THIS IS DONE TOO (#1319 review, Stripe test mode).
    // A refund that settles deletes the booking row, so the press after it — the
    // «Prøv igjen» a 202 offers, once reconciliation or the provider's webhook has
    // confirmed it, or a second tab — finds no booking: `booking_not_found`, never
    // `already_cancelled`. Shown as a message, it left the edit modal open over a
    // calendar still drawing the booking, offering to refund it again. As «done»,
    // «Lukk» closes the modal and the calendar re-reads (`BookingView`).
    if (r.state === 'refused' && r.code === 'booking_not_found') {
      setPhase({ kind: 'done', text: t('booking.edit.refund.notFound') });
      return;
    }
    if (r.state === 'unconfirmed') {
      // Pressing again looks the parked refund up and sends nothing new.
      setPhase({ kind: 'message', text: t('booking.edit.refund.unconfirmed'), retry: true });
      return;
    }
    if (r.state === 'error') {
      setPhase({ kind: 'message', text: t('booking.edit.refund.error'), retry: true });
      return;
    }
    const key = REFUSAL[r.code];
    setPhase({
      kind: 'message',
      text: t(key ?? 'booking.edit.refund.error'),
      retry: key === undefined || RETRYABLE.has(r.code),
    });
  };

  const done = phase.kind === 'done';
  const working = phase.kind === 'working';
  const offerConfirm =
    phase.kind === 'asking' || working || (phase.kind === 'message' && phase.retry);

  return (
    <Dialog
      labelledBy={titleId}
      describedBy={bodyId}
      onClose={() => {
        // Not while the request is out: closing then would hide the answer.
        if (working) return;
        if (done) onDone();
        else onClose();
      }}
      showClose={false}
      backdropClassName=""
      backdropStyle={{
        position: 'fixed',
        inset: 0,
        zIndex: 10002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15,22,17,0.6)',
      }}
      className="overflow-x-hidden overflow-y-auto max-h-[92vh]"
      style={{
        maxHeight: 'calc(100dvh - 2rem)',
        background: '#fff',
        maxWidth: 380,
        width: '100%',
        borderRadius: 16,
        boxShadow: '0 25px 60px -10px rgba(0,0,0,0.45)',
      }}
    >
      <div data-testid="staff-refund-dialog" style={{ padding: '18px 20px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AlertCircle size={20} style={{ color: '#dc2626' }} aria-hidden="true" />
          </span>
          <h3
            id={titleId}
            className="font-display text-lg font-semibold"
            style={{ color: '#1a1f1c' }}
          >
            {t('booking.edit.refund.title')}
          </h3>
        </div>
        <div id={bodyId} className="text-sm text-ink-2" style={{ lineHeight: 1.45 }}>
          {done ? (
            <p role="status" data-testid="staff-refund-done" className="text-ink">
              {phase.text}
            </p>
          ) : (
            <>
              <p data-testid="staff-refund-amount">
                {amount
                  ? t('booking.edit.refund.body', {
                      amount: money(amount.amountMinor, amount.currency),
                    })
                  : t('booking.edit.refund.bodyNoAmount')}
                {attachedAssets > 0 && (
                  <> {t('booking.edit.refund.withAssets', { n: String(attachedAssets) })}</>
                )}
              </p>
              <p className="mt-1 text-xs text-ink-3">{t('booking.edit.refund.irreversible')}</p>
            </>
          )}
        </div>
        {phase.kind === 'message' && (
          <p
            role="alert"
            data-testid="staff-refund-message"
            className="text-sm text-rose-700 mt-3"
            style={{ lineHeight: 1.45 }}
          >
            {phase.text}
          </p>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '0 20px 18px' }}>
        <button
          type="button"
          onClick={done ? onDone : onClose}
          disabled={working}
          className="flex-1 text-sm font-semibold py-2.5 rounded-lg disabled:opacity-40"
          style={{ border: '1px solid #d7dad3', color: '#4a544c', background: '#fff' }}
        >
          {done || phase.kind === 'message'
            ? t('booking.edit.refund.close')
            : t('booking.edit.refund.keep')}
        </button>
        {offerConfirm && (
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={working}
            data-testid="staff-refund-confirm"
            className="flex-1 text-sm font-semibold py-2.5 rounded-lg disabled:opacity-60"
            style={{ background: '#dc2626', color: '#fff', border: 'none' }}
          >
            {working
              ? t('booking.edit.refund.working')
              : phase.kind === 'message'
                ? t('booking.edit.refund.retry')
                : t('booking.edit.refund.confirm')}
          </button>
        )}
      </div>
    </Dialog>
  );
}
